import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { ChatMarkdownRenderer } from '@/components/chat/ChatMarkdownRenderer';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { ExecutionChatInlineForm } from './ExecutionChatInlineForm';
import {
  ExecutionChatResultCardView,
  isExecutionChatResultCard,
} from './ExecutionChatResultCard';
import type {
  ExecutionChatMessageInteraction,
  ExecutionChatMessageRow,
  ExecutionChatQuickAction,
} from '@shared/types/execution-chat';

interface ExecutionChatMessageListProps {
  messages: ExecutionChatMessageRow[];
  isLoading: boolean;
  isSubmitting: boolean;
  isActionEnabled: boolean;
  /** Live streamed assistant text rendered as a provisional bubble. */
  streamingText?: string | null;
  /** Transient status shown before the first streamed text chunk. */
  streamingStatus?: string | null;
  listRef: RefObject<HTMLDivElement>;
  focusStepId?: string | null;
  focusRequestId?: number;
  onScroll: React.UIEventHandler<HTMLDivElement>;
  onRunAct: (
    actionId: string,
    args?: Record<string, unknown>,
    options?: { clientMessage?: string }
  ) => Promise<boolean>;
}

const formatMessageDate = (value: string): string =>
  new Date(value).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

const quickActionToVariant = (
  variant: ExecutionChatQuickAction['variant']
): 'primary' | 'secondary' | 'danger' => {
  if (variant === 'danger') return 'danger';
  if (variant === 'primary') return 'primary';
  return 'secondary';
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFormInteraction = (value: unknown): value is ExecutionChatMessageInteraction =>
  isRecord(value) &&
  value.type === 'form' &&
  isRecord(value.hitl) &&
  typeof value.hitl.kind === 'string';

export const ExecutionChatMessageList = ({
  messages,
  isLoading,
  isSubmitting,
  isActionEnabled,
  streamingText,
  streamingStatus,
  listRef,
  focusStepId = null,
  focusRequestId = 0,
  onScroll,
  onRunAct,
}: ExecutionChatMessageListProps) => {
  const immediateGroupLocksRef = useRef<Set<string>>(new Set());
  const [groupLocks, setGroupLocks] = useState<
    Record<
      string,
      {
        status: 'pending' | 'done';
        actionId: string;
      }
    >
  >({});
  const [messageLocks, setMessageLocks] = useState<
    Record<
      string,
      {
        status: 'pending' | 'done';
        actionId: string;
      }
    >
  >({});
  const [pendingConfirmation, setPendingConfirmation] = useState<{
    messageId: string;
    action: ExecutionChatQuickAction;
    groupId: string;
  } | null>(null);
  const [highlightedStepId, setHighlightedStepId] = useState<string | null>(null);
  const availableActionSlots = useMemo(() => {
    const ids = new Set<string>();
    const messageIds = new Set<string>();
    for (const message of messages) {
      if (message.kind !== 'assistant_message') continue;
      const payload = message.payload as Record<string, unknown>;
      const quickActions = Array.isArray(payload.quickActions)
        ? (payload.quickActions as ExecutionChatQuickAction[]).filter(
            (quickAction) => quickAction.actionId !== 'show_technical_details'
          )
        : [];
      if (quickActions.length > 0) {
        messageIds.add(message.id);
      }
      for (const quickAction of quickActions) {
        ids.add(quickAction.groupId ?? `message-${message.id}`);
      }
    }
    return {
      groupIds: ids,
      messageIds,
    };
  }, [messages]);

  useEffect(() => {
    setGroupLocks((previous) => {
      const next: typeof previous = {};
      for (const [groupId, state] of Object.entries(previous)) {
        if (availableActionSlots.groupIds.has(groupId)) next[groupId] = state;
      }
      return next;
    });
    setMessageLocks((previous) => {
      const next: typeof previous = {};
      for (const [messageId, state] of Object.entries(previous)) {
        if (availableActionSlots.messageIds.has(messageId)) next[messageId] = state;
      }
      return next;
    });
    immediateGroupLocksRef.current = new Set(
      [...immediateGroupLocksRef.current].filter((groupId) =>
        availableActionSlots.groupIds.has(groupId)
      )
    );
  }, [availableActionSlots]);

  useEffect(() => {
    if (!pendingConfirmation) return;
    if (!availableActionSlots.groupIds.has(pendingConfirmation.groupId)) {
      setPendingConfirmation(null);
    }
  }, [availableActionSlots, pendingConfirmation]);

  const runGroupedAction = async (params: {
    messageId: string;
    groupId: string;
    actionId: string;
    args?: Record<string, unknown>;
    clientMessage?: string;
  }): Promise<void> => {
    if (immediateGroupLocksRef.current.has(params.groupId)) return;
    immediateGroupLocksRef.current.add(params.groupId);

    setGroupLocks((previous) => ({
      ...previous,
      [params.groupId]: {
        status: 'pending',
        actionId: params.actionId,
      },
    }));
    setMessageLocks((previous) => ({
      ...previous,
      [params.messageId]: {
        status: 'pending',
        actionId: params.actionId,
      },
    }));

    const ok = await onRunAct(params.actionId, params.args, {
      clientMessage: params.clientMessage,
    });
    setGroupLocks((previous) => {
      if (!previous[params.groupId]) return previous;
      if (!ok) {
        immediateGroupLocksRef.current.delete(params.groupId);
        const { [params.groupId]: _, ...rest } = previous;
        return rest;
      }
      return {
        ...previous,
        [params.groupId]: {
          status: 'done',
          actionId: params.actionId,
        },
      };
    });
    setMessageLocks((previous) => {
      if (!previous[params.messageId]) return previous;
      if (!ok) {
        const { [params.messageId]: _, ...rest } = previous;
        return rest;
      }
      return {
        ...previous,
        [params.messageId]: {
          status: 'done',
          actionId: params.actionId,
        },
      };
    });
  };

  const onQuickActionClick = async (params: {
    event: React.MouseEvent<HTMLButtonElement>;
    messageId: string;
    action: ExecutionChatQuickAction;
    groupId: string;
  }): Promise<void> => {
    params.event.preventDefault();
    params.event.stopPropagation();

    if (!isActionEnabled || isSubmitting) return;
    if (groupLocks[params.groupId]) return;
    if (messageLocks[params.messageId]) return;

    if (params.action.requiresConfirmation) {
      setPendingConfirmation({
        messageId: params.messageId,
        action: params.action,
        groupId: params.groupId,
      });
      return;
    }

    await runGroupedAction({
      messageId: params.messageId,
      groupId: params.groupId,
      actionId: params.action.actionId,
      args: params.action.args,
      clientMessage: params.action.label,
    });
  };

  const renderedMessages = useMemo(() => {
    return messages.map((message) => {
      const payload = message.payload as Record<string, unknown>;
      const isUser = message.actor === 'user' || message.kind === 'user_message';
      const quickActions =
        message.kind === 'assistant_message' && Array.isArray(payload.quickActions)
          ? (payload.quickActions as ExecutionChatQuickAction[]).filter(
              (quickAction) => quickAction.actionId !== 'show_technical_details'
            )
          : [];
      const interaction =
        message.kind === 'assistant_message' && isFormInteraction(payload.interaction)
          ? payload.interaction
          : null;
      const metadata =
        message.kind === 'assistant_message' && isRecord(payload.metadata)
          ? payload.metadata
          : null;
      const resultCard = isExecutionChatResultCard(metadata?.resultCard)
        ? metadata.resultCard
        : null;
      const isPendingConfirmation = pendingConfirmation?.messageId === message.id;
      const isHighlighted = Boolean(
        message.step_id && highlightedStepId === message.step_id
      );
      return (
        <div
          key={message.id}
          data-execution-step-id={message.step_id ?? undefined}
          className={cn(
            'scroll-mt-6 rounded-2xl transition-colors duration-500',
            'flex w-full',
            isUser ? 'justify-end' : 'justify-start',
            isHighlighted && 'bg-domino-lilac/70 ring-1 ring-domino-aubergine/15'
          )}
        >
          <div
            className={cn(
              'max-w-[90%] space-y-2 text-sm leading-relaxed',
              isUser
                ? 'rounded-2xl bg-domino-aubergine px-4 py-3 text-white shadow-sm'
                : 'px-1 py-1 text-domino-ink',
              resultCard && 'w-full max-w-full px-0 py-0'
            )}
          >
            {resultCard ? (
              <ExecutionChatResultCardView card={resultCard} />
            ) : (
              (message.kind === 'user_message' || message.kind === 'assistant_message') &&
              (message.kind === 'assistant_message' ? (
                <ChatMarkdownRenderer
                  content={typeof payload.text === 'string' ? payload.text : ''}
                />
              ) : (
                <p className="whitespace-pre-wrap text-sm leading-relaxed">
                  {typeof payload.text === 'string' ? payload.text : ''}
                </p>
              ))
            )}

            {message.kind === 'assistant_message' && interaction?.type === 'form' && (
              <ExecutionChatInlineForm
                interaction={interaction}
                disabled={!isActionEnabled || isSubmitting}
                onSubmit={async (answer) => {
                  if (!isActionEnabled) return;
                  await onRunAct(
                    'submit_hitl',
                    { answer },
                    {
                      clientMessage: 'Formulaire validé',
                    }
                  );
                }}
                onCancel={async () => {
                  if (!isActionEnabled) return;
                  await onRunAct(
                    'submit_hitl',
                    {
                      answer: { decision: 'cancel' },
                    },
                    {
                      clientMessage: 'Annuler',
                    }
                  );
                }}
              />
            )}

            {message.kind === 'hitl_request' && (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-2 text-xs text-gray-600">
                Validation requise: utilisez les actions du chat pour continuer.
              </div>
            )}

            {message.kind === 'action_result' && (
              <div
                className={cn(
                  'rounded-md px-2 py-1 text-xs',
                  payload.status === 'error'
                    ? 'bg-red-100 text-red-700'
                    : payload.status === 'warning'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-emerald-100 text-emerald-700'
                )}
              >
                {typeof payload.title === 'string' ? `${payload.title} · ` : ''}
                {typeof payload.text === 'string' ? payload.text : ''}
              </div>
            )}

            {message.kind === 'system_event' && (
              <p className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600">
                {typeof payload.text === 'string' ? payload.text : ''}
              </p>
            )}

            {message.kind === 'assistant_message' &&
              interaction === null &&
              quickActions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {quickActions.map((quickAction) => {
                    const groupId = quickAction.groupId ?? `message-${message.id}`;
                    const lock = groupLocks[groupId];
                    const messageLock = messageLocks[message.id];
                    const isLocked =
                      Boolean(lock) ||
                      Boolean(messageLock) ||
                      pendingConfirmation?.groupId === groupId;
                    const isLoadingAction =
                      lock?.status === 'pending' &&
                      lock.actionId === quickAction.actionId;
                    return (
                      <Button
                        key={quickAction.id}
                        size="sm"
                        variant={quickActionToVariant(quickAction.variant)}
                        loading={isLoadingAction}
                        disabled={!isActionEnabled || isSubmitting || isLocked}
                        onClick={(event) => {
                          void onQuickActionClick({
                            event,
                            messageId: message.id,
                            action: quickAction,
                            groupId,
                          });
                        }}
                      >
                        {quickAction.label}
                      </Button>
                    );
                  })}
                </div>
              )}

            {isPendingConfirmation && pendingConfirmation && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-2">
                <p className="text-xs text-amber-800">
                  {pendingConfirmation.action.confirmationMessage ??
                    'Confirmer cette action ?'}
                </p>
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={
                      isSubmitting || Boolean(groupLocks[pendingConfirmation.groupId])
                    }
                    loading={
                      groupLocks[pendingConfirmation.groupId]?.status === 'pending'
                    }
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      void runGroupedAction({
                        messageId: pendingConfirmation.messageId,
                        groupId: pendingConfirmation.groupId,
                        actionId: pendingConfirmation.action.actionId,
                        args: pendingConfirmation.action.args,
                        clientMessage: pendingConfirmation.action.label,
                      });
                      setPendingConfirmation(null);
                    }}
                  >
                    Confirmer
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={isSubmitting}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setPendingConfirmation(null);
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}

            <p className={cn('text-[11px]', isUser ? 'text-gray-300' : 'text-gray-400')}>
              {formatMessageDate(message.created_at)}
            </p>
          </div>
        </div>
      );
    });
  }, [
    groupLocks,
    messageLocks,
    isActionEnabled,
    isSubmitting,
    messages,
    onRunAct,
    pendingConfirmation,
    highlightedStepId,
  ]);

  useEffect(() => {
    if (!focusStepId || focusRequestId < 1) return;

    const container = listRef.current;
    if (!container) return;

    const target = Array.from(
      container.querySelectorAll<HTMLElement>('[data-execution-step-id]')
    ).find((element) => element.dataset.executionStepId === focusStepId);

    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedStepId(focusStepId);

    const timeoutId = window.setTimeout(() => {
      setHighlightedStepId((current) => (current === focusStepId ? null : current));
    }, 1800);

    return () => window.clearTimeout(timeoutId);
  }, [focusStepId, focusRequestId, listRef, messages.length]);

  const trimmedStreamingText = streamingText?.trim() ?? '';
  const trimmedStreamingStatus = streamingStatus?.trim() ?? '';
  const showStreamingBubble =
    trimmedStreamingText.length > 0 || trimmedStreamingStatus.length > 0;

  return (
    <div
      ref={listRef}
      className="flex-1 overflow-y-auto bg-white px-4 py-5 sm:px-6"
      onScroll={onScroll}
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3">
        {isLoading ? (
          <div className="flex h-28 items-center justify-center">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
          </div>
        ) : renderedMessages.length === 0 && !showStreamingBubble ? (
          <div className="px-1 py-1 text-sm text-gray-500">
            Aucun message pour le moment.
          </div>
        ) : (
          <>
            {renderedMessages}
            {showStreamingBubble && (
              <div className="flex justify-start">
                <div className="max-w-[90%] px-1 py-1 text-sm leading-relaxed text-domino-ink">
                  {trimmedStreamingText.length > 0 ? (
                    <ChatMarkdownRenderer content={trimmedStreamingText} />
                  ) : (
                    <span className="text-gray-500">{trimmedStreamingStatus}</span>
                  )}
                  <span className="ml-0.5 inline-block h-3.5 w-1.5 translate-y-0.5 animate-pulse rounded-sm bg-domino-ink/40" />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
